import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    LuBold, LuItalic, LuUnderline, LuStrikethrough,
    LuList, LuListOrdered, LuLink, LuUnlink, LuPaperclip, LuRemoveFormatting
} from 'react-icons/lu';
import { FiCheck, FiX } from 'react-icons/fi';
import {
    esHtmlSinContenidoVisible, esHtmlVacio, escaparHtml, htmlDesdeTextoPlano, normalizarUrl, sanitizarHtml,
    RE_EMAIL_FINAL, RE_URL_FINAL
} from './sanitizarHtml';
import './RichTextEditor.css';

/**
 * Editor de texto enriquecido propio (sustituye a react-quill en el chat de tickets).
 *
 * - Negrita, cursiva, subrayado, tachado, listas ordenadas/desordenadas.
 * - Enlaces manuales (Ctrl/Cmd + K) y autodetección al escribir o pegar.
 * - Adjuntar archivos desde la barra, arrastrándolos al editor o pegándolos.
 * - Devuelve HTML compatible con el que ya guardaba Quill (<p>, <strong>, <ul>, <a>…).
 *
 * Es un componente controlado: `value` entra como HTML y `onChange` lo devuelve
 * saneado en el momento del envío (ver sanitizarHtml en el componente padre).
 */

const COMANDOS_ESTADO = {
    negrita: 'bold',
    cursiva: 'italic',
    subrayado: 'underline',
    tachado: 'strikeThrough',
    listaVinetas: 'insertUnorderedList',
    listaNumerada: 'insertOrderedList',
};

/** execCommand sigue siendo la vía más fiable para formatear un contenteditable. */
const comandoNativo = (comando, valor = null) => {
    try {
        return document.execCommand(comando, false, valor);
    } catch (error) {
        return false;
    }
};

const ESTADO_INICIAL = {
    negrita: false,
    cursiva: false,
    subrayado: false,
    tachado: false,
    listaVinetas: false,
    listaNumerada: false,
    enlace: false,
};

const RichTextEditor = ({
    value = '',
    onChange,
    onFiles,
    onSubmit,
    dropZoneRef,
    placeholder = 'Escribe un mensaje…',
    disabled = false,
    className = '',
}) => {
    const contenedorRef = useRef(null);
    const editorRef = useRef(null);
    const inputArchivosRef = useRef(null);
    const inputEnlaceRef = useRef(null);
    const ultimoHtmlRef = useRef('');
    const rangoGuardadoRef = useRef(null);
    // Los listeners nativos de arrastre se registran una vez: leen el callback por ref
    const onFilesRef = useRef(onFiles);
    onFilesRef.current = onFiles;

    const [formatos, setFormatos] = useState(ESTADO_INICIAL);
    const [vacio, setVacio] = useState(esHtmlSinContenidoVisible(value));
    const [arrastrando, setArrastrando] = useState(false);
    const [popover, setPopover] = useState(null); // { url, texto, editando, error }

    // ── Configuración base de execCommand ────────────────────────────────────
    useEffect(() => {
        try {
            document.execCommand('defaultParagraphSeparator', false, 'p');
            document.execCommand('styleWithCSS', false, false);
        } catch (error) {
            // Navegadores que no soportan estos flags: el comportamiento por defecto vale
        }
    }, []);

    // ── Sincronización con el valor del padre ────────────────────────────────
    useEffect(() => {
        const el = editorRef.current;
        if (!el) return;

        const entrante = value || '';
        if (entrante === ultimoHtmlRef.current) return;

        el.innerHTML = entrante;
        ultimoHtmlRef.current = entrante;
        setVacio(esHtmlSinContenidoVisible(entrante));
    }, [value]);

    const emitirCambio = useCallback(() => {
        const el = editorRef.current;
        if (!el) return;

        let html = el.innerHTML;
        // El placeholder se decide con el contenido real del editor: una lista sin
        // texto todavía no es "mensaje", pero sí ocupa sitio y debe ocultarlo
        setVacio(esHtmlSinContenidoVisible(el.innerHTML));

        if (esHtmlVacio(html)) html = '';
        ultimoHtmlRef.current = html;
        if (onChange) onChange(html);
    }, [onChange]);

    // ── Selección y estado de la barra ───────────────────────────────────────
    const enlaceDeLaSeleccion = useCallback(() => {
        const el = editorRef.current;
        const seleccion = window.getSelection();
        if (!el || !seleccion || seleccion.rangeCount === 0) return null;

        let nodo = seleccion.anchorNode;
        while (nodo && nodo !== el) {
            if (nodo.nodeType === Node.ELEMENT_NODE && nodo.tagName === 'A') return nodo;
            nodo = nodo.parentNode;
        }
        return null;
    }, []);

    const seleccionDentroDelEditor = useCallback(() => {
        const el = editorRef.current;
        const seleccion = window.getSelection();
        if (!el || !seleccion || seleccion.rangeCount === 0) return false;
        return el.contains(seleccion.getRangeAt(0).commonAncestorContainer);
    }, []);

    const refrescarFormatos = useCallback(() => {
        if (!seleccionDentroDelEditor()) return;

        const estado = { ...ESTADO_INICIAL };
        Object.entries(COMANDOS_ESTADO).forEach(([clave, comando]) => {
            try {
                estado[clave] = document.queryCommandState(comando);
            } catch (error) {
                estado[clave] = false;
            }
        });
        estado.enlace = Boolean(enlaceDeLaSeleccion());
        setFormatos(estado);
    }, [enlaceDeLaSeleccion, seleccionDentroDelEditor]);

    useEffect(() => {
        const manejar = () => refrescarFormatos();
        document.addEventListener('selectionchange', manejar);
        return () => document.removeEventListener('selectionchange', manejar);
    }, [refrescarFormatos]);

    const guardarRango = useCallback(() => {
        const seleccion = window.getSelection();
        const valida = seleccion && seleccion.rangeCount > 0 && seleccionDentroDelEditor();
        // Se guarda (o se descarta) siempre: un rango viejo aplicaría el enlace donde no toca
        rangoGuardadoRef.current = valida ? seleccion.getRangeAt(0).cloneRange() : null;
    }, [seleccionDentroDelEditor]);

    const restaurarRango = useCallback(() => {
        const el = editorRef.current;
        const rango = rangoGuardadoRef.current;
        if (!el) return false;

        el.focus();
        if (!rango) return false;

        const seleccion = window.getSelection();
        seleccion.removeAllRanges();
        seleccion.addRange(rango);
        return true;
    }, []);

    // ── Comandos de formato ──────────────────────────────────────────────────
    const ejecutar = useCallback((comando, valor = null) => {
        if (disabled) return;
        editorRef.current?.focus();
        comandoNativo(comando, valor);
        emitirCambio();
        refrescarFormatos();
    }, [disabled, emitirCambio, refrescarFormatos]);

    const limpiarFormato = useCallback(() => {
        if (disabled) return;
        editorRef.current?.focus();
        comandoNativo('removeFormat');
        comandoNativo('unlink');
        emitirCambio();
        refrescarFormatos();
    }, [disabled, emitirCambio, refrescarFormatos]);

    // ── Autodetección de enlaces mientras se escribe ─────────────────────────
    /**
     * Busca una URL o email justo antes del cursor y lo convierte en <a>.
     * `caracterFinal` se inserta tras el enlace (el espacio que iba a escribirse)
     * para que el texto siguiente no herede el formato de enlace.
     */
    const enlazarAntesDelCursor = useCallback((caracterFinal = '') => {
        const el = editorRef.current;
        const seleccion = window.getSelection();
        if (!el || !seleccion || seleccion.rangeCount === 0 || !seleccion.isCollapsed) return false;

        const nodo = seleccion.anchorNode;
        if (!nodo || nodo.nodeType !== Node.TEXT_NODE || !el.contains(nodo)) return false;
        if (enlaceDeLaSeleccion()) return false;

        const textoPrevio = nodo.textContent.slice(0, seleccion.anchorOffset);
        const coincidencia = textoPrevio.match(RE_URL_FINAL) || textoPrevio.match(RE_EMAIL_FINAL);
        if (!coincidencia) return false;

        const bruto = coincidencia[0];
        const recorte = bruto.match(/[.,;:!?)\]}]+$/);
        const visible = recorte ? bruto.slice(0, -recorte[0].length) : bruto;
        if (visible.length < 4) return false;

        const href = normalizarUrl(visible);
        if (!href) return false;

        const inicio = textoPrevio.length - bruto.length;
        const rango = document.createRange();
        rango.setStart(nodo, inicio);
        rango.setEnd(nodo, inicio + visible.length);

        const enlace = document.createElement('a');
        enlace.href = href;
        enlace.target = '_blank';
        enlace.rel = 'noopener noreferrer';
        enlace.textContent = visible;

        rango.deleteContents();
        rango.insertNode(enlace);

        const nuevoRango = document.createRange();
        if (caracterFinal) {
            const cola = document.createTextNode(caracterFinal);
            enlace.parentNode.insertBefore(cola, enlace.nextSibling);
            nuevoRango.setStart(cola, cola.length);
        } else {
            nuevoRango.setStartAfter(enlace);
        }
        nuevoRango.collapse(true);
        seleccion.removeAllRanges();
        seleccion.addRange(nuevoRango);

        return true;
    }, [enlaceDeLaSeleccion]);

    // ── Popover de enlace manual ─────────────────────────────────────────────
    const abrirPopoverEnlace = useCallback(() => {
        if (disabled) return;

        guardarRango();
        const enlaceActual = enlaceDeLaSeleccion();
        const seleccion = window.getSelection();
        const textoSeleccionado = seleccion && !seleccion.isCollapsed ? seleccion.toString() : '';

        setPopover({
            url: enlaceActual ? enlaceActual.getAttribute('href') || '' : '',
            texto: enlaceActual ? enlaceActual.textContent : textoSeleccionado,
            editando: Boolean(enlaceActual),
            error: '',
        });
    }, [disabled, enlaceDeLaSeleccion, guardarRango]);

    useEffect(() => {
        if (popover) inputEnlaceRef.current?.focus();
    }, [popover]);

    const cerrarPopover = useCallback((devolverFoco = true) => {
        setPopover(null);
        if (devolverFoco) restaurarRango();
    }, [restaurarRango]);

    const marcarEnlacesNuevos = useCallback(() => {
        editorRef.current?.querySelectorAll('a').forEach((enlace) => {
            enlace.setAttribute('target', '_blank');
            enlace.setAttribute('rel', 'noopener noreferrer');
        });
    }, []);

    const aplicarEnlace = useCallback(() => {
        if (!popover) return;

        const href = normalizarUrl(popover.url);
        if (!href) {
            setPopover((prev) => ({ ...prev, error: 'Introduce una dirección válida' }));
            return;
        }

        restaurarRango();
        const seleccion = window.getSelection();
        const enlaceActual = enlaceDeLaSeleccion();
        const textoFinal = (popover.texto || '').trim() || href;

        if (enlaceActual) {
            enlaceActual.setAttribute('href', href);
            enlaceActual.textContent = textoFinal;
        } else if (seleccion && !seleccion.isCollapsed) {
            comandoNativo('createLink', href);
        } else {
            comandoNativo(
                'insertHTML',
                `<a href="${escaparHtml(href)}" target="_blank" rel="noopener noreferrer">${escaparHtml(textoFinal)}</a>&nbsp;`
            );
        }

        marcarEnlacesNuevos();
        setPopover(null);
        editorRef.current?.focus();
        emitirCambio();
        refrescarFormatos();
    }, [popover, restaurarRango, enlaceDeLaSeleccion, marcarEnlacesNuevos, emitirCambio, refrescarFormatos]);

    /**
     * `desdePopover` distingue las dos vías: desde el popover hay que recuperar el
     * rango guardado (el foco estaba en el input), desde la barra la selección sigue viva.
     */
    const quitarEnlace = useCallback((desdePopover = false) => {
        if (desdePopover) {
            restaurarRango();
        } else {
            editorRef.current?.focus();
        }

        const enlaceActual = enlaceDeLaSeleccion();
        if (enlaceActual) {
            const rango = document.createRange();
            rango.selectNodeContents(enlaceActual);
            const seleccion = window.getSelection();
            seleccion.removeAllRanges();
            seleccion.addRange(rango);
        }
        comandoNativo('unlink');
        setPopover(null);
        editorRef.current?.focus();
        emitirCambio();
        refrescarFormatos();
    }, [restaurarRango, enlaceDeLaSeleccion, emitirCambio, refrescarFormatos]);

    // ── Teclado ──────────────────────────────────────────────────────────────
    const manejarKeyDown = (evento) => {
        if (disabled) return;

        const conModificador = evento.ctrlKey || evento.metaKey;

        if (conModificador && evento.key === 'Enter') {
            evento.preventDefault();
            if (enlazarAntesDelCursor()) emitirCambio();
            if (onSubmit) onSubmit();
            return;
        }

        if (conModificador && evento.key.toLowerCase() === 'k') {
            evento.preventDefault();
            abrirPopoverEnlace();
            return;
        }

        if (evento.key === ' ') {
            if (enlazarAntesDelCursor(' ')) {
                evento.preventDefault();
                emitirCambio();
            }
            return;
        }

        if (evento.key === 'Enter' && enlazarAntesDelCursor()) {
            emitirCambio();
        }
    };

    const manejarBlur = () => {
        if (enlazarAntesDelCursor()) emitirCambio();
    };

    // ── Pegado ───────────────────────────────────────────────────────────────
    const manejarPaste = (evento) => {
        if (disabled) return;

        const datos = evento.clipboardData;
        if (!datos) return;

        const archivos = Array.from(datos.files || []);
        if (archivos.length > 0) {
            evento.preventDefault();
            if (onFiles) onFiles(archivos);
            return;
        }

        evento.preventDefault();
        const html = datos.getData('text/html');
        const texto = datos.getData('text/plain');
        const contenido = html ? sanitizarHtml(html) : htmlDesdeTextoPlano(texto);
        if (!contenido) return;

        comandoNativo('insertHTML', contenido);
        emitirCambio();
        refrescarFormatos();
    };

    // ── Arrastrar y soltar archivos ──────────────────────────────────────────
    /**
     * La escucha se hace con listeners nativos sobre `dropZoneRef` (por ejemplo el
     * chat completo del ticket) o, si no se pasa, sobre el propio editor. Arrastrar
     * en cualquier punto de esa zona enciende la animación del editor y soltar
     * adjunta los archivos, se suelte donde se suelte.
     */
    useEffect(() => {
        const zona = dropZoneRef?.current || contenedorRef.current;
        if (!zona) return undefined;

        let profundidad = 0; // dragenter/dragleave se disparan también al cruzar hijos
        const traeArchivos = (evento) => Array.from(evento.dataTransfer?.types || []).includes('Files');

        const alEntrar = (evento) => {
            if (disabled || !traeArchivos(evento)) return;
            evento.preventDefault();
            profundidad += 1;
            setArrastrando(true);
        };

        const alPasarPorEncima = (evento) => {
            if (disabled || !traeArchivos(evento)) return;
            evento.preventDefault();
            evento.dataTransfer.dropEffect = 'copy';
            setArrastrando(true);
        };

        const alSalir = (evento) => {
            if (!traeArchivos(evento)) return;
            profundidad = Math.max(0, profundidad - 1);
            if (profundidad === 0) setArrastrando(false);
        };

        const alSoltar = (evento) => {
            if (disabled || !traeArchivos(evento)) return;
            evento.preventDefault();
            profundidad = 0;
            setArrastrando(false);

            const archivos = Array.from(evento.dataTransfer.files || []);
            if (archivos.length > 0 && onFilesRef.current) onFilesRef.current(archivos);
        };

        zona.addEventListener('dragenter', alEntrar);
        zona.addEventListener('dragover', alPasarPorEncima);
        zona.addEventListener('dragleave', alSalir);
        zona.addEventListener('drop', alSoltar);

        return () => {
            zona.removeEventListener('dragenter', alEntrar);
            zona.removeEventListener('dragover', alPasarPorEncima);
            zona.removeEventListener('dragleave', alSalir);
            zona.removeEventListener('drop', alSoltar);
        };
    }, [disabled, dropZoneRef]);

    const manejarArchivosSeleccionados = (evento) => {
        const archivos = Array.from(evento.target.files || []);
        if (archivos.length > 0 && onFiles) onFiles(archivos);
        evento.target.value = '';
    };

    // ── Barra de herramientas ────────────────────────────────────────────────
    const botonesFormato = [
        { clave: 'negrita', comando: 'bold', icono: LuBold, titulo: 'Negrita (Ctrl+B)' },
        { clave: 'cursiva', comando: 'italic', icono: LuItalic, titulo: 'Cursiva (Ctrl+I)' },
        { clave: 'subrayado', comando: 'underline', icono: LuUnderline, titulo: 'Subrayado (Ctrl+U)' },
        { clave: 'tachado', comando: 'strikeThrough', icono: LuStrikethrough, titulo: 'Tachado' },
    ];

    const botonesLista = [
        { clave: 'listaVinetas', comando: 'insertUnorderedList', icono: LuList, titulo: 'Lista de viñetas' },
        { clave: 'listaNumerada', comando: 'insertOrderedList', icono: LuListOrdered, titulo: 'Lista numerada' },
    ];

    const renderBoton = ({ clave, comando, icono: Icono, titulo }) => (
        <button
            key={clave}
            type="button"
            className={`rte-btn ${formatos[clave] ? 'rte-btn--activo' : ''}`}
            title={titulo}
            aria-label={titulo}
            aria-pressed={formatos[clave]}
            disabled={disabled}
            onMouseDown={(evento) => evento.preventDefault()}
            onClick={() => ejecutar(comando)}
        >
            <Icono />
        </button>
    );

    return (
        <div
            ref={contenedorRef}
            className={`rte ${arrastrando ? 'rte--arrastrando' : ''} ${disabled ? 'rte--deshabilitado' : ''} ${className}`.trim()}
        >
            <div className="rte-toolbar" role="toolbar" aria-label="Opciones de formato">
                {botonesFormato.map(renderBoton)}

                <span className="rte-sep" aria-hidden="true" />
                {botonesLista.map(renderBoton)}

                <span className="rte-sep" aria-hidden="true" />
                <button
                    type="button"
                    className={`rte-btn ${formatos.enlace ? 'rte-btn--activo' : ''}`}
                    title="Insertar enlace (Ctrl+K)"
                    aria-label="Insertar enlace"
                    disabled={disabled}
                    onMouseDown={(evento) => evento.preventDefault()}
                    onClick={abrirPopoverEnlace}
                >
                    <LuLink />
                </button>
                {formatos.enlace && (
                    <button
                        type="button"
                        className="rte-btn"
                        title="Quitar enlace"
                        aria-label="Quitar enlace"
                        disabled={disabled}
                        onMouseDown={(evento) => evento.preventDefault()}
                        onClick={() => quitarEnlace(false)}
                    >
                        <LuUnlink />
                    </button>
                )}
                <button
                    type="button"
                    className="rte-btn"
                    title="Quitar formato"
                    aria-label="Quitar formato"
                    disabled={disabled}
                    onMouseDown={(evento) => evento.preventDefault()}
                    onClick={limpiarFormato}
                >
                    <LuRemoveFormatting />
                </button>

                {onFiles && (
                    <>
                        <span className="rte-sep" aria-hidden="true" />
                        <button
                            type="button"
                            className="rte-btn"
                            title="Adjuntar archivos"
                            aria-label="Adjuntar archivos"
                            disabled={disabled}
                            onMouseDown={(evento) => evento.preventDefault()}
                            onClick={() => inputArchivosRef.current?.click()}
                        >
                            <LuPaperclip />
                        </button>
                        <input
                            ref={inputArchivosRef}
                            type="file"
                            multiple
                            className="rte-input-archivos"
                            onChange={manejarArchivosSeleccionados}
                        />
                    </>
                )}
            </div>

            <div
                ref={editorRef}
                className="rte-content"
                contentEditable={!disabled}
                suppressContentEditableWarning
                role="textbox"
                aria-multiline="true"
                aria-label="Editor de mensaje"
                data-vacio={vacio ? 'true' : 'false'}
                data-placeholder={placeholder}
                spellCheck
                onInput={emitirCambio}
                onKeyDown={manejarKeyDown}
                onKeyUp={refrescarFormatos}
                onMouseUp={refrescarFormatos}
                onBlur={manejarBlur}
                onPaste={manejarPaste}
            />

            {popover && (
                <div className="rte-popover" onMouseDown={(evento) => evento.stopPropagation()}>
                    <div className="rte-popover-campos">
                        <label className="rte-popover-campo">
                            <span>Dirección</span>
                            <input
                                ref={inputEnlaceRef}
                                type="text"
                                value={popover.url}
                                placeholder="https://ejemplo.com"
                                onChange={(evento) => setPopover((prev) => ({ ...prev, url: evento.target.value, error: '' }))}
                                onKeyDown={(evento) => {
                                    if (evento.key === 'Enter') { evento.preventDefault(); aplicarEnlace(); }
                                    if (evento.key === 'Escape') { evento.preventDefault(); cerrarPopover(); }
                                }}
                            />
                        </label>
                        <label className="rte-popover-campo">
                            <span>Texto</span>
                            <input
                                type="text"
                                value={popover.texto}
                                placeholder="Texto del enlace"
                                onChange={(evento) => setPopover((prev) => ({ ...prev, texto: evento.target.value }))}
                                onKeyDown={(evento) => {
                                    if (evento.key === 'Enter') { evento.preventDefault(); aplicarEnlace(); }
                                    if (evento.key === 'Escape') { evento.preventDefault(); cerrarPopover(); }
                                }}
                            />
                        </label>
                    </div>

                    {popover.error && <p className="rte-popover-error">{popover.error}</p>}

                    <div className="rte-popover-acciones">
                        <button type="button" className="rte-popover-btn rte-popover-btn--principal" onClick={aplicarEnlace}>
                            <FiCheck /> {popover.editando ? 'Actualizar' : 'Insertar'}
                        </button>
                        {popover.editando && (
                            <button type="button" className="rte-popover-btn" onClick={() => quitarEnlace(true)}>
                                <LuUnlink /> Quitar
                            </button>
                        )}
                        <button type="button" className="rte-popover-btn" onClick={() => cerrarPopover()}>
                            <FiX /> Cancelar
                        </button>
                    </div>
                </div>
            )}

            {arrastrando && (
                <div className="rte-drop">
                    <LuPaperclip />
                    <p>Suelta los archivos para adjuntarlos</p>
                </div>
            )}
        </div>
    );
};

export default RichTextEditor;
